import React, { useMemo } from 'react';

import { Formik } from 'formik';

import { useDepartmentsBudgetsReportContext } from './context/DepartmentsBudgetsReportContext';
import { TYPE_DETAILED, TYPE_SUMMARY } from './types/ReportType';

import Button from 'CommonComponents/Button';
import MultiSelectField from 'CommonComponents/MultiSelectField';
import SelectField from 'CommonComponents/SelectField';
import ToggleFormField from 'CommonComponents/ToggleFormField';

const GenerateDepartmentsBudgetsReportCriteria = () => {
    const { criteria, updateCriteria, configs, markUpCategories, isVikEnabled } = useDepartmentsBudgetsReportContext();
    const baseFilters = useMemo(
        () => [
            {
                label: $t('Cash'),
                value: 'include_cash',
            },
            ...(isVikEnabled
                ? [
                      {
                          label: $t('VIK'),
                          value: 'include_vik',
                      },
                  ]
                : []),
            {
                label: $t('Detailed Description'),
                value: 'include_detailed_description',
            },
            {
                label: $t('Workforce details'),
                value: 'include_workforce',
            },
        ],
        [$t],
    );

    const INCLUDES = useMemo(
        () => [
            ...baseFilters,
            ...(markUpCategories || []).map((markUpCategory) => ({
                label: markUpCategory.name,
                value: `include_markup_${markUpCategory.id}`,
            })),
        ],
        [baseFilters, markUpCategories],
    );

    const markupInitialValues = useMemo(() => {
        return (markUpCategories || []).reduce((acc, category) => {
            acc[`include_markup_${category.id}`] = false;
            return acc;
        }, {});
    }, [markUpCategories]);

    const initialValues = useMemo(
        () => ({
            ...criteria,
            ...markupInitialValues,
        }),
        [markupInitialValues],
    );

    return (
        <Formik
            initialValues={initialValues}
            onSubmit={(values, actions) => {
                updateCriteria(values);
                actions.setSubmitting(false);
            }}
        >
            {({ values, isSubmitting, handleSubmit, setFieldValue }) => {
                return (
                    <div className="tw-px-6 tw-py-7 tw-space-y-4 tw-rounded-lg tw-bg-background">
                        <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-6 tw-gap-x-10 tw-gap-y-5">
                            <div className="lg:tw-col-span-2 tw-space-y-4">
                                <div className="tw-flex tw-flex-col tw-space-y-1.5">
                                    <label className="text-input__label">{$t('Budget Version')}</label>
                                    <SelectField
                                        className="tw-block tw-w-full tw-rounded-lg tw-text-sm !tw-bg-grey/50 tw-text-charcoal tw-font-semibold placeholder:tw-text-charcoal-75 placeholder:tw-text-sm placeholder:tw-font-normal"
                                        options={configs.budgetVersions}
                                        value={values.budget_id}
                                        backgroundColor="#e4e4e480"
                                        isDisabled={isSubmitting}
                                        clearable
                                        onChange={(value) => setFieldValue('budget_id', value ? value.value : null)}
                                    />
                                </div>
                                <div className="tw-flex tw-flex-col tw-space-y-1.5">
                                    <label className="text-input__label">{$t('Departments')}</label>
                                    <MultiSelectField
                                        className="tw-block tw-w-full tw-rounded-lg tw-text-sm !tw-bg-grey/50 tw-text-charcoal tw-font-semibold placeholder:tw-text-charcoal-75 placeholder:tw-text-sm placeholder:tw-font-normal"
                                        options={configs.departmentsOptions}
                                        value={values.department_ids}
                                        backgroundColor="#e4e4e480"
                                        isDisabled={isSubmitting}
                                        hideSelectedOptions={false}
                                        isGrouped
                                        showIndentation
                                        clearable
                                        onChange={(values) => {
                                            setFieldValue(
                                                'department_ids',
                                                values.map((val) => val.value),
                                            );
                                        }}
                                    />
                                </div>
                            </div>
                            <div className="lg:tw-col-span-4">
                                <div>
                                    <div className="tw-flex tw-flex-col tw-space-y-1.5">
                                        <label className="text-input__label">{$t('Type')}</label>
                                        <div className="tw-h-8 tw-flex tw-items-center tw-gap-x-5">
                                            <label className="tw-flex tw-items-center tw-gap-x-2 tw-text-sm tw-font-semibold tw-text-charcoal-50">
                                                <input
                                                    type="radio"
                                                    name="type"
                                                    disabled={isSubmitting}
                                                    value={TYPE_SUMMARY}
                                                    checked={!values.include_details}
                                                    onChange={() => setFieldValue('include_details', false)}
                                                />
                                                {$t('Summary')}
                                            </label>
                                            <label className="tw-flex tw-items-center tw-gap-x-2 tw-text-sm tw-font-semibold tw-text-charcoal-50">
                                                <input
                                                    type="radio"
                                                    name="type"
                                                    disabled={isSubmitting}
                                                    value={TYPE_DETAILED}
                                                    checked={values.include_details}
                                                    onChange={() => setFieldValue('include_details', true)}
                                                />
                                                {$t('Detailed')}
                                            </label>
                                        </div>
                                    </div>
                                </div>
                                <div className="tw-space-y-1.5">
                                    <label className="text-input__label">{$t('Options')}</label>
                                    <div className="tw-min-h-8 tw-grid tw-grid-cols-4 tw-gap-y-1.5 tw-gap-x-5">
                                        {INCLUDES.map((option) => (
                                            <ToggleFormField
                                                key={option.value}
                                                label={option.label}
                                                name={option.value}
                                                checked={values[option.value]}
                                                disabled={isSubmitting || (option.disabled && option.disabled(values))}
                                                onChange={() => {
                                                    const newVal = !values[option.value];
                                                    setFieldValue(option.value, newVal);
                                                }}
                                            />
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="tw-flex tw-justify-end">
                            <Button className="disabled:tw-bg-blue-25" variant="primary" onClick={handleSubmit} disabled={isSubmitting} loading={isSubmitting}>
                                {$t('Generate Report')}
                            </Button>
                        </div>
                    </div>
                );
            }}
        </Formik>
    );
};

export default GenerateDepartmentsBudgetsReportCriteria;
